<template>
  <div class="b-table" :class="rooClasses">
    <b-table-mobile-sort
      v-if="mobileCards && hasSortablenewColumns"
      :current-sort-column="currentSortColumn"
      :sort-multiple="sortMultiple"
      :sort-multiple-data="sortMultipleDataComputed"
      :is-asc="isAsc"
      :columns="newColumns"
      :placeholder="mobileSortPlaceholder"
      :icon-pack="iconPack"
      :sort-icon="sortIcon"
      :sort-icon-size="sortIconSize"
      @sort="(column, event) => sort(column, null, event)"
      @removePriority="(column) => removeSortingPriority(column)"
    />
    <div
      v-if="paginated && (paginationPosition === 'top' || paginationPosition === 'both')"
      class="top level">
      <div class="level-left">
        <slot name="top-left"/>
      </div>

      <div class="level-right">
        <div v-if="paginated" class="level-item">
          <b-pagination
            :icon-pack="iconPack"
            :total="newDataTotal"
            :per-page="perPage"
            :simple="paginationSimple"
            :size="paginationSize"
            :current="newCurrentPage"
            @change="pageChanged"
            :aria-next-label="ariaNextLabel"
            :aria-previous-label="ariaPreviousLabel"
            :aria-page-label="ariaPageLabel"
            :aria-current-label="ariaCurrentLabel" />
        </div>
      </div>
    </div>
    <div class="sticky-header" ref="stickyHeader">
      <table class="table modern-table-table">
        <thead v-if="newColumns.length">
          <tr>
            <th
              v-for="(column, index) in visibleColumns"
              :key="index"
              ref="headerCell"
              :class="[column.headerClass, {
                                'is-current-sort': !sortMultiple && currentSortColumn === column,
                                'is-sortable': column.sortable,
                                'is-sticky': column.sticky,
                                'is-unselectable': !column.headerSelectable
                            }]"
              :style="{
                                width: column.width === undefined ? null :
                                (isNaN(column.width) ? column.width : column.width + 'px'),
                                left: column.sticky ? calcLeftPosition(index) : null
                            }"
              @click.stop="sort(column, null, $event)">
              <div
                class="th-wrap"
                :class="{
                                    'is-numeric': column.numeric,
                                    'is-centered': column.centered
                            }">
                <template v-if="column.$scopedSlots && column.$scopedSlots.header">
                  <b-slot-component
                    :component="column"
                    :scoped="true"
                    name="header"
                    tag="span"
                    :props="{ column, index }"
                  />
                </template>
                <template v-else-if="$scopedSlots.header">
                  <slot
                    name="header"
                    :column="column"
                    :index="index"
                  />
                </template>
                <template v-else>{{ column.label }}</template>

                <template
                  v-if="sortMultiple &&
                                        sortMultipleDataComputed &&
                                        sortMultipleDataComputed.length > 0 &&
                                        sortMultipleDataComputed.filter(i =>
                                    i.field === column.field).length > 0">
                  <b-icon
                    :icon="sortIcon"
                    :pack="iconPack"
                    both
                    :size="sortIconSize"
                    :class="{
                                            'is-desc': sortMultipleDataComputed.filter(i =>
                                                i.field === column.field)[0].order === 'desc'
                                        }"
                  />
                  {{ findIndexOfSortData(column) }}
                  <button
                    class="delete is-small multi-sort-cancel-icon"
                    type="button"
                    @click.stop="removeSortingPriority(column)"/>
                </template>

                <b-icon
                  v-else-if="column.sortable && !sortMultiple"
                  :icon="sortIcon"
                  :pack="iconPack"
                  both
                  :size="sortIconSize"
                  :class="{
                                        'is-desc': !isAsc,
                                        'is-invisible': currentSortColumn !== column
                                    }"
                />
              </div>
            </th>
          </tr>
          <tr v-if="hasCustomSubheadings" class="is-subheading">
            <th v-if="showDetailRowIcon" width="40px"/>
            <th v-if="checkable && checkboxPosition === 'left'" />
            <th
              v-for="(column, index) in visibleColumns"
              :key="index"
              :style="{
                                width: column.width === undefined ? null
                            : (isNaN(column.width) ? column.width : column.width + 'px') }">
              <div
                class="th-wrap"
                :class="{
                                    'is-numeric': column.numeric,
                                    'is-centered': column.centered
                            }">
                <template
                  v-if="column.$scopedSlots && column.$scopedSlots.subheading"
                >
                  <b-slot-component
                    :component="column"
                    :scoped="true"
                    name="subheading"
                    tag="span"
                    :props="{ column, index }"
                  />
                </template>
                <template v-else-if="$scopedSlots.subheading">
                  <slot
                    name="subheading"
                    :column="column"
                    :index="index"
                  />
                </template>
                <template v-else>{{ column.subheading }}</template>
              </div>
            </th>
            <th v-if="checkable && checkboxPosition === 'right'" />
          </tr>
          <tr v-if="hasSearchablenewColumns">
            <th v-if="showDetailRowIcon" width="40px"/>
            <th v-if="checkable && checkboxPosition === 'left'" />
            <th
              v-for="(column, index) in visibleColumns"
              :key="index"
              :style="{
                                width: column.width === undefined ? null
                            : (isNaN(column.width) ? column.width : column.width + 'px') }">
              <div class="th-wrap">
                <template v-if="column.searchable">
                  <slot
                    v-if="$scopedSlots.searchable"
                    name="searchable"
                    :column="column"
                    :filters="filters"
                  />
                  <b-input
                    v-else
                    @[filtersEvent].native="onFiltersEvent"
                    v-model="filters[column.field]"
                    :type="column.numeric ? 'number' : 'text'" />
                </template>
              </div>
            </th>
            <th v-if="checkable && checkboxPosition === 'right'" />
          </tr>
        </thead>
      </table>
    </div>
    <div
      class="table-wrapper"
      ref="tableWrapper"
      :class="tableWrapperClasses"
      :style="{
                height: height === undefined ? null :
                (isNaN(height) ? height : height + 'px')
            }"
    >
      <table
        class="table modern-table-table"
        :class="tableClasses"
        :tabindex="!focusable ? false : 0"
        @keydown.self.prevent.up="pressedArrow(-1)"
        @keydown.self.prevent.down="pressedArrow(1)">
        <thead v-if="newColumns.length" class="hidden-header">
        <tr>
          <th v-if="showDetailRowIcon" width="40px"/>
          <th class="checkbox-cell" v-if="checkable && checkboxPosition === 'left'">
            <template v-if="headerCheckable">
              <b-checkbox
                :value="isAllChecked"
                :disabled="isAllUncheckable"
                @change.native="checkAll"/>
            </template>
          </th>
          <th
            v-for="(column, index) in visibleColumns"
            :key="index"
            :class="[column.headerClass, {
                'is-current-sort': !sortMultiple && currentSortColumn === column,
                'is-sortable': column.sortable,
                'is-sticky': column.sticky,
                'is-unselectable': !column.headerSelectable
            }]"
            :style="{
                                width: column.width === undefined ? null :
                                (isNaN(column.width) ? column.width : column.width + 'px')
                            }"
            @click.stop="sort(column, null, $event)">
          </th>
          <th class="checkbox-cell" v-if="checkable && checkboxPosition === 'right'">
            <template v-if="headerCheckable">
              <b-checkbox
                :value="isAllChecked"
                :disabled="isAllUncheckable"
                @change.native="checkAll"/>
            </template>
          </th>
        </tr>
        </thead>
        <tbody v-if="visibleData.length">
        <template v-for="(row, index) in visibleData">
          <tr
            :key="customRowKey ? row[customRowKey] : index"
            :class="[rowClass(row, index), {
                                'is-selected': row === selected,
                                'is-checked': isRowChecked(row),
                            }]"
            @click="selectRow(row)"
            @dblclick="$emit('dblclick', row)"
            @mouseenter="$listeners.mouseenter ? $emit('mouseenter', row) : null"
            @mouseleave="$listeners.mouseleave ? $emit('mouseleave', row) : null"
            @contextmenu="$emit('contextmenu', row, $event)"
            :draggable="draggable"
            @dragstart="handleDragStart($event, row, index)"
            @dragend="handleDragEnd($event, row, index)"
            @drop="handleDrop($event, row, index)"
            @dragover="handleDragOver($event, row, index)"
            @dragleave="handleDragLeave($event, row, index)">

            <td
              v-if="showDetailRowIcon"
              class="chevron-cell"
            >
              <a
                v-if="hasDetailedVisible(row)"
                role="button"
                @click.stop="toggleDetails(row)">
                <b-icon
                  icon="chevron-right"
                  :pack="iconPack"
                  both
                  :class="{'is-expanded': isVisibleDetailRow(row)}"/>
              </a>
            </td>

            <td
              class="checkbox-cell"
              v-if="checkable && checkboxPosition === 'left'">
              <b-checkbox
                :disabled="!isRowCheckable(row)"
                :value="isRowChecked(row)"
                @click.native.prevent.stop="checkRow(row, index, $event)"
              />
            </td>

            <slot
              v-if="$scopedSlots.default"
              :row="row"
              :index="index"
            />
            <template v-else>
              <BTableColumn
                v-for="column in newColumns"
                v-bind="column"
                :key="column.customKey || column.label"
                internal>
                                    <span
                                      v-if="column.renderHtml"
                                      v-html="getValueByPath(row, column.field)"
                                    />
                <template v-else>
                  {{ getValueByPath(row, column.field) }}
                </template>
              </BTableColumn>
            </template>

            <td
              class="checkbox-cell"
              v-if="checkable && checkboxPosition === 'right'">
              <b-checkbox
                :disabled="!isRowCheckable(row)"
                :value="isRowChecked(row)"
                @click.native.prevent.stop="checkRow(row, index, $event)"
              />
            </td>
          </tr>

          <!-- Do not add `key` here (breaks details) -->
          <!-- eslint-disable-next-line -->
          <tr
            v-if="isActiveDetailRow(row)"
            class="detail">
            <td :colspan="columnCount">
              <div class="detail-container">
                <slot
                  name="detail"
                  :row="row"
                  :index="index"/>
              </div>
            </td>
          </tr>
          <slot
            v-if="isActiveCustomDetailRow(row)"
            name="detail"
            :row="row"
            :index="index"
          />
        </template>
        </tbody>
        <tbody v-else>
        <tr class="is-empty">
          <td :colspan="columnCount">
            <slot name="empty"/>
          </td>
        </tr>
        </tbody>
        <tfoot v-if="$slots.footer !== undefined">
        <tr class="table-footer">
          <slot name="footer" v-if="hasCustomFooterSlot()"/>
          <th :colspan="columnCount" v-else>
            <slot name="footer"/>
          </th>
        </tr>
        </tfoot>
      </table>
    </div>

    <div ref="bottomScroll" class="sticky-scroll">
      <div class="scroll" :style="{width: `${commonWidth}px`}">-</div>
    </div>

    <div
      v-if="(checkable && hasBottomLeftSlot()) ||
            (paginated && (paginationPosition === 'bottom' || paginationPosition === 'both'))"
      class="level">
      <div class="level-left-left">
        <slot name="bottom-left-left"/>
      </div>
      <div class="level-left">
        <slot name="bottom-left"/>
      </div>

      <div class="level-right">
        <div v-if="paginated" class="level-item">
          <b-pagination
            :icon-pack="iconPack"
            :total="newDataTotal"
            :per-page="perPage"
            :simple="paginationSimple"
            :size="paginationSize"
            :current="newCurrentPage"
            @change="pageChanged"
            :aria-next-label="ariaNextLabel"
            :aria-previous-label="ariaPreviousLabel"
            :aria-page-label="ariaPageLabel"
            :aria-current-label="ariaCurrentLabel" />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { BTable } from 'buefy/src/components/table';
import { getValueByPath } from 'buefy/src/utils/helpers';
import { toInteger } from 'lodash';

export default {
  name: 'ModernTable',
  extends: BTable,

  data() {
    return {
      sortMultipleDataLocal: [],
      getValueByPath,
      newColumns: [...this.columns],
      visibleDetailRows: this.openedDetailed,
      newData: this.data,
      newDataTotal: this.backendPagination ? this.total : this.data.length,
      newCheckedRows: [...this.checkedRows],
      lastCheckedRowIndex: null,
      newCurrentPage: this.currentPage,
      currentSortColumn: {},
      isAsc: true,
      filters: {},
      firstTimeSort: true, // Used by first time initSort
      // eslint-disable-next-line vue/no-reserved-keys
      _isTable: true, // Used by TableColumn
      widths: [],
      commonWidth: 0,
    };
  },


  mounted() {
    this.checkPredefinedDetailedRows();
    this.checkSort();

    const wrapper = this.$refs.tableWrapper;
    const scroll = this.$refs.bottomScroll;
    const header = this.$refs.stickyHeader;

    wrapper.addEventListener('scroll', () => {
      this.$nextTick(() => {
        scroll.scrollLeft = wrapper.scrollLeft;
        header.scrollLeft = wrapper.scrollLeft;
      });
    });
    scroll.addEventListener('scroll', () => {
      wrapper.scrollLeft = scroll.scrollLeft;
      header.scrollLeft = scroll.scrollLeft;
    });
    header.addEventListener('scroll', () => {
      wrapper.scrollLeft = header.scrollLeft;
      scroll.scrollLeft = header.scrollLeft;
    });
  },

  activated() {
    this.$nextTick(() => {
      this.updateScroll();
      this.columnWidthByEmail();
    });
  },

  updated() {
    this.$nextTick(() => {
      this.updateScroll();
      this.columnWidthByEmail();
    });
  },

  methods: {
    updateScroll() {
      const scroll = this.$refs.bottomScroll.querySelector('.scroll');

      let scrollWidth = 0;
      if (this.$refs.headerCell.length) {
        for (let i = 0; i < this.visibleColumns.length; i += 1) {
          scrollWidth += parseInt(this.visibleColumns[i].width, 10);
        }
        scroll.style.width = `${scrollWidth}px`;
      }
    },

    scrollTop() {
      this.$nextTick(() => {
        window.scrollTo(0, 0);
        const wrapper = this.$refs.tableWrapper;
        wrapper.scrollLeft = 0;
      });
    },
    pageChanged(page) {
      this.scrollTop();
      this.newCurrentPage = page > 0 ? page : 1;
      this.$emit('page-change', this.newCurrentPage);
      this.$emit('update:currentPage', this.newCurrentPage);
    },
    columnWidthByEmail() {
      const scroll = this.$refs.bottomScroll.querySelector('.scroll');
      let scrollWidth = scroll.getBoundingClientRect().width;
      const tableRows = this.$refs.tableWrapper.querySelectorAll('tbody tr');
      const widths = [];
      if (tableRows.length) {
        for (let i = 0; i < tableRows.length; i += 1) {
          const el = tableRows[i].firstElementChild.querySelector('.email');
          if (el) { widths.push(el.clientWidth + 40); }
        }
      }
      const defaultWidth = this.visibleColumns[0].width
        ? parseInt(this.visibleColumns[0].width, 10) : 0;
      if (Math.max(...widths) > defaultWidth) {
        scrollWidth += Math.max(...widths) - defaultWidth;
        scroll.style.width = `${scrollWidth}px`;
      }
      const maxWidth = Math.max(Math.max(...widths), defaultWidth);
      const firstChild = this.$refs.tableWrapper.querySelector('thead tr').firstElementChild;
      firstChild.style.width = `${maxWidth}px`;
      if (this.$refs.headerCell.length) {
        this.$refs.headerCell[0].style.width = `${maxWidth}px`;
      }
    },
    calcLeftPosition(index) {
      let width = 0;
      for (let i = 0; i < index; i += 1) {
        if (this.visibleColumns[i].sticky) {
          width += toInteger(this.visibleColumns[i].width);
        }
      }
      return `${width}px`;
    },
  },
};
</script>

<style scoped lang="stylus">
@require "../assets/style/modern/variables.styl";
.level-left-left
  width stretch
  @media (max-width: $breakpoints.md)
    margin-bottom 12px
.table-wrapper
  margin-bottom 0 !important
  -ms-overflow-style none
  scrollbar-width none
  &::-webkit-scrollbar
    display none
.level
  margin-top 12px

.modern-table-table
  td
    font-size 18px

.table-footer th
  height 1px

.sticky-header
  position sticky
  top 68px
  z-index 10
  overflow-x scroll
  -ms-overflow-style none
  scrollbar-width none
  &::-webkit-scrollbar
    display none

.hidden-header th
  color white
  height 0px !important
  padding 0
  margin 0
  border none

.sticky-scroll
  display block
  position sticky
  bottom 0
  z-index 10
  overflow-x auto
  overflow-y hidden
  .scroll
    height 1px
    color transparent

@media screen and (max-width: 768px)
  .sticky-scroll
    display none
  .sticky-header
    display none

</style>
